// React Query hook for the current admin's profile + permission flags.
// Backed by `GET /v1/admin/admin-users/me` (sub-plan 07 row 6).
//
// Cache key namespaced as ["admin", "me"] so DataTable list-cache keys
// (which start with the endpoint key) cannot collide.
//
// Loading/error semantics (sub-plan 13 §13.4 sidebar gating):
//   - During isLoading or on error, sidebar should render only Dashboard +
//     Sign out (loading-safe minimal state).
//   - Optional chaining (perms?.can_*) handles the bootstrap case where
//     admin_permissions is null.

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";

export interface AdminProfile {
  id: string;
  email: string | null;
  display_name?: string | null;
  // Migration 00011 added these. All optional for the loading-shape case.
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  phone?: string | null;
  is_active?: boolean | null;
}

export interface AdminPermissions {
  can_manage_admins?: boolean | null;
  can_manage_app_users?: boolean | null;
  can_manage_jobs?: boolean | null;
  can_send_push?: boolean | null;
  can_view_reports?: boolean | null;
  can_manage_pricing?: boolean | null;
  can_manage_withdrawals?: boolean | null;
}

export interface AdminMeResponse {
  profile: AdminProfile;
  admin_permissions: AdminPermissions | null;
}

export function useAdminMe() {
  return useQuery<AdminMeResponse>({
    queryKey: ["admin", "me"],
    queryFn: () => apiFetch<AdminMeResponse>("/v1/admin/admin-users/me"),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
